import { useEffect, useMemo, useState } from "react";
import CalendarHeader from "../CalendarHeader";
import ButtonPlayNow from "../ButtonPlayNow";
import CalendarDays from "../CalendarDays";
import {
  format,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  startOfWeek,
  startOfMonth,
  addWeeks,
  isToday,
  addMonths,
  startOfDay,
} from "date-fns";
import SyncManager from "../../../api/SyncManager";
import { useSession } from "../../../context/SessionProvider";
import { Wrapper } from "./CalendarSection.components";

const CalendarSection: React.FC = () => {
  const [selectedDay, setSelectedDay] = useState<string>(startOfDay(new Date()).toString());
  const { wonHistory, setWonHistory, userData, selectMonth, setSelectMonth } = useSession();

  const toDay = addMonths(new Date(), selectMonth);
  const thisMonthNow = toDay.getMonth();
  const toDayInfo = useMemo(() => {
    return {
      month: toDay.getMonth() + 1,
      date: toDay.getDate(),
      year: toDay.getFullYear(),
      monthName: format(toDay, "MMMM"),
    };
  }, [toDay]);

  useEffect(() => {
    SyncManager.loadDailySudokuForMonths(toDayInfo.year.toString(), toDayInfo.month.toString().padStart(2, "0"));
  }, [toDayInfo]);

  useEffect(() => {
    setWonHistory(
      SyncManager.getCompletedGamesInMonth(toDayInfo.year.toString(), toDayInfo.month.toString().padStart(2, "0"))
    );
  }, [setWonHistory, toDayInfo.month, toDayInfo.year, userData]);

  const firstDayMonthIsMonday = () => {
    if (format(startOfMonth(toDay), "e", { weekStartsOn: 1 }) === "1") {
      return true;
    }
    return false;
  };

  const lastDayMonthIsSunday = () => {
    if (format(endOfMonth(toDay), "e", { weekStartsOn: 1 }) === "7") {
      return true;
    }
    return false;
  };

  const startMonth = startOfMonth(toDay);
  const endMonth = endOfMonth(toDay);

  const startCalendar = firstDayMonthIsMonday()
    ? addWeeks(startMonth, -1)
    : startOfWeek(startOfMonth(toDay), { weekStartsOn: 1 });
  const endCalendar = lastDayMonthIsSunday()
    ? addWeeks(endMonth, 1)
    : endOfWeek(endOfMonth(toDay), { weekStartsOn: 1 });

  const calendarDatesFullInfo = eachDayOfInterval({
    start: startCalendar,
    end: endCalendar,
  });

  const isDayCompleted = (day: Date) => {
    if (wonHistory == null) {
      return;
    }

    if (!wonHistory.length) {
      return false;
    }

    const dayStr = day.getDate().toString().padStart(2, "0");

    const dayHistory = wonHistory.find((item: any) => {
      return item.day === dayStr;
    });

    return !!dayHistory;
  };

  const calendarDates = calendarDatesFullInfo.map((day) => {
    return {
      fullDate: day,
      month: day.getMonth(),
      date: day.getDate(),
      year: day.getFullYear(),
      monthName: format(day, "MMMM"),
      isToDay: isToday(day),
      isWon: isDayCompleted(day),
    };
  });

  return (
    <Wrapper>
      <CalendarHeader monthOffset={selectMonth} setMonthOffset={setSelectMonth} toDayInfo={toDayInfo} />
      <CalendarDays
        thisMonthNow={thisMonthNow}
        calendarDates={calendarDates}
        selectedDay={selectedDay}
        setSelectedDay={setSelectedDay}
      />
      <ButtonPlayNow selectedDay={selectedDay} />
    </Wrapper>
  );
};

export default CalendarSection;
